import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import {
  getTierConfig,
  updateTierConfig,
  deleteTierConfig,
} from 'services/tierConfigService';
import { tierConfigSchema } from './tierConfigSchema';

const DEFAULT_TIERS = [
  { name: 'PLATINUM', label: 'Platinum', color: '#E5E4E2', minOrders: 50, minSpent: 1500, order: 3 },
  { name: 'GOLD', label: 'Gold', color: '#FFD700', minOrders: 20, minSpent: 500, order: 2 },
  { name: 'SILVER', label: 'Silver', color: '#C0C0C0', minOrders: 5, minSpent: 100, order: 1 },
  { name: 'BRONZE', label: 'Bronze', color: '#CD7F32', minOrders: 0, minSpent: 0, order: 0 },
];
const DEFAULT_TIER = 'BRONZE';
const DEFAULT_TIER_COLOR = '#CD7F32';

function addIds(tiers) {
  return (tiers || []).map((t) => ({
    ...t,
    id: t.id || uuidv4(),
  }));
}

function tiersToPayload(tiers) {
  return tiers.map(({ id, ...rest }) => rest);
}

function deriveNameFromLabel(label) {
  if (!label || typeof label !== 'string') return '';
  return label
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_');
}

export function useTierConfig() {
  const { currentBusiness } = useSelector(({ business }) => business);
  const businessGroupUuid = currentBusiness?.groupUuids?.[0];

  const [tiers, setTiers] = useState([]);
  const [defaultTier, setDefaultTierState] = useState(DEFAULT_TIER);
  const [defaultTierColor, setDefaultTierColor] = useState(DEFAULT_TIER_COLOR);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isUsingDefaults, setIsUsingDefaults] = useState(true);
  const [validationErrors, setValidationErrors] = useState(null);

  const loadConfig = useCallback(async () => {
    if (!businessGroupUuid) {
      setIsLoading(false);
      setTiers(addIds(DEFAULT_TIERS));
      setDefaultTierState(DEFAULT_TIER);
      setDefaultTierColor(DEFAULT_TIER_COLOR);
      setIsUsingDefaults(true);
      return;
    }
    setIsLoading(true);
    try {
      const res = await getTierConfig(businessGroupUuid);
      const data = res?.data?.data ?? res?.data ?? res;
      const tierList = data?.tiers ?? DEFAULT_TIERS;
      const hasCustomConfig = Array.isArray(data?.tiers) && data.tiers.length > 0;
      setTiers(addIds(tierList));
      setDefaultTierState(data?.defaultTier ?? DEFAULT_TIER);
      setDefaultTierColor(data?.defaultTierColor ?? DEFAULT_TIER_COLOR);
      setIsUsingDefaults(!hasCustomConfig);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load tier config');
      setTiers(addIds(DEFAULT_TIERS));
      setDefaultTierState(DEFAULT_TIER);
      setDefaultTierColor(DEFAULT_TIER_COLOR);
      setIsUsingDefaults(true);
    } finally {
      setIsLoading(false);
      setIsDirty(false);
      setValidationErrors(null);
    }
  }, [businessGroupUuid]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const addTier = useCallback(() => {
    const maxOrder = Math.max(0, ...tiers.map((t) => t.order), -1);
    setTiers((prev) => [
      ...prev,
      {
        id: uuidv4(),
        name: '',
        label: '',
        color: '#808080',
        minOrders: 0,
        minSpent: 0,
        order: maxOrder + 1,
      },
    ]);
    setIsDirty(true);
  }, [tiers]);

  const removeTier = useCallback((id) => {
    if (tiers.length <= 1) return;
    setTiers((prev) => {
      const next = prev.filter((t) => t.id !== id);
      const removed = prev.find((t) => t.id === id);
      if (removed?.name === defaultTier) {
        const fallback = next.sort((a, b) => b.order - a.order)[next.length - 1];
        if (fallback) {
          setDefaultTierState(fallback.name);
          setDefaultTierColor(fallback.color);
        }
      }
      return next;
    });
    setIsDirty(true);
  }, [tiers.length, defaultTier]);

  const updateTier = useCallback((id, updates, options = {}) => {
    const { autoDeriveName = true } = options;
    setTiers((prev) =>
      prev.map((t) => {
        if (t.id !== id) return t;
        const next = { ...t, ...updates };
        if (autoDeriveName && 'label' in updates && updates.label !== undefined && !('name' in updates)) {
          next.name = deriveNameFromLabel(updates.label) || t.name;
        }
        return next;
      })
    );
    setIsDirty(true);
  }, []);

  const reorderTiers = useCallback((newOrderedTiers) => {
    const withOrder = newOrderedTiers.map((t, index) => ({
      ...t,
      order: newOrderedTiers.length - 1 - index,
    }));
    setTiers(withOrder);
    setIsDirty(true);
  }, []);

  const setDefaultTier = useCallback((tierName, tierColor) => {
    setDefaultTierState(tierName);
    setDefaultTierColor(tierColor ?? defaultTierColor);
    setIsDirty(true);
  }, [defaultTierColor]);

  const validate = useCallback(() => {
    setValidationErrors(null);
    try {
      tierConfigSchema.validateSync(
        { defaultTier, defaultTierColor, tiers },
        { abortEarly: false }
      );
      return true;
    } catch (err) {
      const errors = { tiers: [] };
      (err?.inner || []).forEach((e) => {
        const path = e.path || '';
        const match = path.match(/^tiers\[(\d+)\]\.(.+)$/);
        if (match) {
          const idx = parseInt(match[1], 10);
          const field = match[2];
          while (errors.tiers.length <= idx) errors.tiers.push({});
          if (!errors.tiers[idx][field]) errors.tiers[idx][field] = [];
          errors.tiers[idx][field].push(e.message);
        } else {
          if (!errors[path]) errors[path] = [];
          errors[path].push(e.message);
        }
      });
      setValidationErrors(errors);
      return false;
    }
  }, [defaultTier, defaultTierColor, tiers]);

  const save = useCallback(async () => {
    if (!businessGroupUuid) {
      toast.error('No business group selected');
      return;
    }
    if (!validate()) {
      toast.error('Please fix validation errors before saving');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        defaultTier,
        defaultTierColor,
        tiers: tiersToPayload(tiers),
      };
      await updateTierConfig(businessGroupUuid, payload);
      toast.success('Tier configuration saved');
      setIsUsingDefaults(false);
      setIsDirty(false);
      setValidationErrors(null);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to save tier config';
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  }, [businessGroupUuid, defaultTier, defaultTierColor, tiers, validate]);

  const applyPreset = useCallback(({ tiers: presetTiers, defaultTier: presetDefault, defaultTierColor: presetColor }) => {
    setTiers(presetTiers || []);
    setDefaultTierState(presetDefault || DEFAULT_TIER);
    setDefaultTierColor(presetColor || DEFAULT_TIER_COLOR);
    setIsDirty(true);
    setIsUsingDefaults(false);
  }, []);

  const resetToDefaults = useCallback(async () => {
    if (!businessGroupUuid) {
      setTiers(addIds(DEFAULT_TIERS));
      setDefaultTierState(DEFAULT_TIER);
      setDefaultTierColor(DEFAULT_TIER_COLOR);
      setIsUsingDefaults(true);
      setIsDirty(false);
      return;
    }
    try {
      await deleteTierConfig(businessGroupUuid);
      toast.success('Tier config reset to defaults');
      await loadConfig();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to reset tier config');
    }
  }, [businessGroupUuid, loadConfig]);

  return {
    businessGroupUuid,
    tiers,
    defaultTier,
    defaultTierColor,
    isLoading,
    isSaving,
    isDirty,
    isUsingDefaults,
    validationErrors,
    addTier,
    removeTier,
    updateTier,
    reorderTiers,
    setDefaultTier,
    save,
    resetToDefaults,
    loadConfig,
    validate,
    applyPreset,
  };
}
